import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePermissions } from '@/hooks/usePermission'
import { useOnline } from '@/hooks/useOnline'
import { checklistApi, type ChecklistTransport } from '@/lib/jobChecklist'

export function JobChecklist({ jobId }: { jobId: string }) {
  const { can } = usePermissions()
  const online = useOnline()
  return <ChecklistPanel key={jobId} jobId={jobId} canEdit={can('jobs.edit')} canCheck={can('jobs.complete') || can('jobs.edit')} online={online} />
}

/** Transport injection keeps public fixtures synthetic and gives MOB-09 a queue seam. */
export function ChecklistPanel({ jobId, canEdit, canCheck, online, transport = checklistApi }: {
  jobId: string; canEdit: boolean; canCheck: boolean; online: boolean; transport?: ChecklistTransport
}) {
  const client = useQueryClient()
  const key = ['job-checklist', jobId]
  const [label, setLabel] = useState('')
  const [draft, setDraft] = useState<{ id: string; label: string } | null>(null)
  const query = useQuery({ queryKey: key, queryFn: () => transport.list(jobId), retry: false })
  const mutation = useMutation({
    mutationFn: async (action: { kind: 'add'; id: string; label: string } | { kind: 'check'; id: string; checked: boolean; revision: number }) => {
      if (!online) throw new Error('Offline')
      return action.kind === 'add'
        ? transport.add(jobId, action.id, action.label)
        : transport.set(jobId, action.id, { checked: action.checked, revision: action.revision })
    },
    onSuccess: (_item, action) => {
      if (action.kind === 'add') { setLabel(''); setDraft(null) }
    },
    onSettled: () => client.invalidateQueries({ queryKey: key }),
  })
  const busy = mutation.isPending || query.isFetching
  const items = query.data ?? []
  return (
    <section className="ds-tile-inset p-4 space-y-3" aria-label="Job checklist">
      <h3 className="text-base font-semibold">Job checklist</h3>
      <p className="text-sm" style={{ color: 'var(--midnight-text3)' }}>Checking items does not mark the job complete.</p>
      {query.isPending ? <p role="status">Loading checklist…</p> : query.isError ? (
        <div role="alert"><p>Couldn’t load the checklist. Completion is unknown.</p><button type="button" className="btn-secondary min-h-11 px-4" onClick={() => void query.refetch()}>Retry loading</button></div>
      ) : <>
        {items.length === 0 ? <p>No checklist items yet. Nothing has been checked.</p> : <>
          <p role="status">{items.filter((item) => item.checked).length} of {items.length} checked</p>
          <ul className="space-y-2">{items.map((item) => <li key={item.id}>
            <label className="flex items-center gap-3 min-h-11 cursor-pointer" style={{ overflowWrap: 'anywhere' }}>
              <input type="checkbox" className="h-5 w-5 shrink-0" checked={item.checked} disabled={!canCheck || !online || busy}
                onChange={(event) => mutation.mutate({ kind: 'check', id: item.id, checked: event.target.checked, revision: item.revision })} />
              <span className="min-w-0">{item.label}</span>
            </label>
          </li>)}</ul>
        </>}
        {canEdit && <form className="space-y-2" onSubmit={(event) => {
          event.preventDefault()
          if (!label.trim() || busy || !online) return
          const next = draft?.label === label.trim() ? draft : { id: crypto.randomUUID(), label: label.trim() }
          setDraft(next)
          mutation.mutate({ kind: 'add', ...next })
        }}>
          <label className="block text-sm" htmlFor={`checklist-${jobId}`}>New checklist item</label>
          <input id={`checklist-${jobId}`} className="input-field w-full" maxLength={240} value={label} disabled={busy || !online} onChange={(event) => setLabel(event.target.value)} />
          <button type="submit" className="btn-secondary min-h-11 px-4" disabled={!label.trim() || busy || !online}>Add item</button>
        </form>}
      </>}
      {!canCheck && <p className="text-sm">Read-only checklist.</p>}
      {!online && <p role="status">Offline. Checklist changes aren’t queued yet. Reconnect to make changes.</p>}
      {mutation.isPending && <p role="status">Saving…</p>}
      {mutation.isError && <p role="alert">Change not confirmed. Reload the checklist before retrying; another crew member may have changed it.</p>}
    </section>
  )
}
